import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonDatetime,
  IonFooter,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonModal,
  IonNote,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonText,
  IonToast,
} from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactI18NextChild, useTranslation } from 'react-i18next';
import { closeOutline } from 'ionicons/icons';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMaskito } from '@maskito/react';
import ReactQuill from 'react-quill';
import moment from 'moment';
import { useAuth } from '../hooks/authContext';
import FosSelectItem from '../atom/FosSelectItem';
import { Option } from '../utils/Interfaces';

import './ClaimNotesForm.css';
import './ClaimNotificationForm.css';
import useClaimDetails from '../hooks/useClaimDetails';

import { emailRegexNoGlobal, phoneRegex } from '../pages/helper/FormsHelper';
import getRestClient, { ClientType } from '../utils/AxiosClient';
import AutocompleteSearch from '../molecule/AutocompleteSearch';
import getGraphQLClient from '../hooks/graphQLClientUtil';
import GET_EMAIL_SUGGESTIONS, { EmailSuggestion } from '../graphql/GetEmailSuggestions';
import { CustomEmailContact } from './OtherEmailsModal';
import FormItemDivider from '../atom/FormItemDivider';
import languages from '../supportedLanguages';
import { PermissionsList, usePermissions } from '../hooks/permissions';
import useUserProfileInfo from '../hooks/userProfileInfo';

type Props = {
  userName: string;
  claimIndx?: string;
  notification?: any;
  onSubmit: (success: boolean) => void;
  defaultPhone?: string;
};

export enum MessageTemplate {
  ArrivalConfirmation = 'ArrivalConfirmation',
  AppointmentNotification = 'AppointmentNotification',
  OnTheWayCrew = 'OnTheWayCrew',
}

export enum MessageChannel {
  Email = 'Email',
  SMS = 'SMS',
}

export enum TimeSelector {
  SpecificTime,
  TimeRange,
}

type FormData = {
  claimIndx: number;
  type: string;
  template: string;
  phone: string;
  date: string;
  time: string;
  startTime: string;
  endTime: string;
  who: string;
  message: string;
  recipients: string[];
  ccRecipients: string[];
  messageTemplate: MessageTemplate;
  messageChannel: MessageChannel;
  sendToXa: boolean;
  contactEmail: string;
};

const convertStringTimeToDateTime = (timeString: string) => {
  // Split the time string into hours, minutes, and seconds
  const [hours, minutes, seconds] = timeString.split(':').map(Number);

  // Create a new Date object with the current date and the parsed time
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);

  return date;
};

const ClaimNotificationForm: React.FC<Props> = ({ userName, claimIndx, notification, onSubmit, defaultPhone }) => {
  const { t, i18n } = useTranslation();
  const tEn = i18n.getFixedT('en');
  const tFr = i18n.getFixedT('fr');
  const { user, userState } = useAuth()!;

  const region = userState.userAuthPayload?.regionId!;

  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [defaultWho, setDefaultWho] = useState('');
  const [claim, setClaim] = useState<any>();
  const graphQLClient = getGraphQLClient(userState);
  const [ccSuggestions, setCcSuggestions] = useState<Array<any>>([]);
  const [suggestions, setSuggestions] = useState<Array<any>>([]);
  const [selectedEmails, setSelectedEmails] = useState<Array<CustomEmailContact>>([]);
  const [ccRecipients, setCcRecipients] = useState<Array<CustomEmailContact>>([]);
  const [isInvalidCcEmailAlertOpen, setInvalidCcEmailAlert] = useState(false);
  const [isLinkedToXact, setIsLinkedToXact] = useState(false);
  const [isSendToXact, setIsSendToXact] = useState(false);
  const [phaseIndxWithXA, setPhaseIndxWithXA] = useState(0);
  const tempValue = 'temp';
  const { permissions } = usePermissions();
  const [selectedTimeSelector, setSelectedTimeSelector] = useState(TimeSelector.SpecificTime);
  const isCrewNotificationsEnabled = permissions.includes(PermissionsList.CrewNotification);
  const generateModal = useRef<HTMLIonModalElement>(null);
  const [showSendToXaModal, setShowSendToXaModal] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');
  const { userInfo } = useUserProfileInfo(userState, region, user?.userGuid ?? '');
  const userContact = selectedEmails?.length > 0 ? selectedEmails?.[0].phoneCell || selectedEmails?.[0].email : (userInfo?.phoneCell ?? userState.userAuthPayload?.preferredUsername);
  const phoneMask = useMaskito({
    options: {
      mask: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    },
  });
  const dayOptions: Intl.DateTimeFormatOptions = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
  };
  const hourOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric', // Display the hour (e.g., 1, 2, ..., 12)
    minute: 'numeric', // Display the minutes (e.g., 00, 01, ..., 59)
    hour12: true, // Use 12-hour format (AM/PM)
  };
  const hourWithSecondsOptions: Intl.DateTimeFormatOptions = {
    hour12: false, // Use 24-hour format
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };

  const mapToCustomEmailContact = (email: string): CustomEmailContact => ({
    email,
    id: tempValue,
    selected: true,
    fullName: email,
  });

  const onClaimDetailsSuccess = async (_data: any) => {
    if (_data) {
      // use data
      setClaim(_data.claim);
      if (notification?.ccAddresses) {
        const mappedCcRecipients = notification.ccAddresses.map(mapToCustomEmailContact);
        setCcRecipients(mappedCcRecipients);
      }
    }
  };

  const { isFetching } = useClaimDetails(region, !claimIndx ? 0 : Number(claimIndx), userState, onClaimDetailsSuccess);
  const typeOptions: Option[] = [
    { label: t('Email'), value: MessageChannel.Email },
    { label: t('SMS'), value: MessageChannel.SMS },
  ];

  const isEdit = notification && Object.keys(notification).length > 0;

  const schema = yup
    .object({
      type: yup.string().required(),
      template: yup.string().required(t('templateRequired')),
      who: yup.string()
        .typeError(t('WhoRequired')),
      message: yup.string(),
      date: yup
        .string()
        .typeError(t('dateRequired'))
        .required(t('dateRequired')),
      time: yup
        .string()
        .typeError(t('timeRequired'))
        .when('template', {
          is: () => selectedTimeSelector === TimeSelector.SpecificTime,
          then: yup.string().typeError(t('timeRequired')).required(t('timeRequired')),
        }),
      startTime: yup
        .string()
        .typeError(t('timeRequired'))
        .when('template', {
          is: () => selectedTimeSelector === TimeSelector.TimeRange,
          then: yup.string().typeError(t('timeRequired')).required(t('timeRequired')),
        }),
      endTime: yup
        .string()
        .typeError(t('timeRequired'))
        .when('template', {
          is: () => selectedTimeSelector === TimeSelector.TimeRange,
          then: yup.string().typeError(t('timeRequired')).required(t('timeRequired')),
        }),
      phone: yup.string().when({
        is: (val: string) => val?.length > -1,
        then: yup.string().matches(phoneRegex, { message: t('invalidPhoneNumber') }),
      }),
      contactEmail: yup.string().when('type', {
        is: (type: string) => !claim?.projEmail && !isEdit && type === MessageChannel.Email,
        then: yup.string().typeError(t('emailRequired'))
          .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/g, { message: t('pleaseEnterAValidEmailAddress') })
          .required(t('emailRequired')),
      }),
    })
    .required();

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    control,
    setValue,
    clearErrors,
    trigger,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: MessageChannel.Email,
      phone: (defaultPhone !== t('phoneNumberPlaceHolder')) ? defaultPhone : undefined,
      template: isCrewNotificationsEnabled
        ? MessageTemplate.OnTheWayCrew
        : MessageTemplate.ArrivalConfirmation,
    },
  });

  const { template, type, phone, message, date, time, startTime, endTime, contactEmail } = watch();

  const isAppointmentNotificationSelected = template === MessageTemplate.AppointmentNotification;
  // Will need to change the below line if new crew forms are added.
  const isOnTheWayCrewSelected = template === MessageTemplate.OnTheWayCrew;

  const isArrivalConfirmationSelected = template === MessageTemplate.ArrivalConfirmation;
  const emailInputRef = useRef('');
  const ccEmailInputRef = useRef('');

  const isWhoEnable = isAppointmentNotificationSelected || isArrivalConfirmationSelected;

  useEffect(() => {
    if (claim && Array.isArray(claim.phases)) {
      claim.phases.forEach((element: { linkedToXA: boolean; phaseIndx: number }) => {
        if (element.linkedToXA && !isLinkedToXact) {
          setIsLinkedToXact(true);
          setIsSendToXact(true);
        }
      });
    }
  }, [claim, isLinkedToXact, phaseIndxWithXA]);

  const formattedNotifactionXact = (recipients?: string, dateTime?: string, sender?: string, messageType?: string, messageBody?: string, ccRecipients?: string[]) => {
    if (isLinkedToXact) {
      let notificationNoteFormattedText;
      if (ccRecipients && ccRecipients.length > 0) {
        notificationNoteFormattedText =
          `Recipients:&nbsp;${recipients
          }<br>Cc Recipients:&nbsp;${ccRecipients.join(', ')
          }<br>Date/Time Sent:&nbsp;${dateTime
          }<br>Sender:&nbsp;${sender
          }<br>Message Type:&nbsp;${messageType
          }<br>Message Body:<br>`;
      } else {
        notificationNoteFormattedText =
          `Recipients:&nbsp;${recipients
          }<br>Date/Time Sent:&nbsp;${dateTime
          }<br>Sender:&nbsp;${sender
          }<br>Message Type:&nbsp;${messageType
          }<br>Message Body:<br>`;
      }
      return notificationNoteFormattedText;
    }
    const noXa = '';
    return noXa;
  };

  /*
   * Submit form
   * */
  const onSubmitHandler = handleSubmit(async () => {
    try {
      setIsLoading(true);
      const communication: any = {};
      const whoProp =
        selectedEmails?.length > 0 ? selectedEmails[0].email : userState.userAuthPayload?.preferredUsername;
      const whoPropEmployeeName = selectedEmails?.length > 0 ? selectedEmails[0].fullName : userName;
      let mappedCcEmails: string[] = [];
      ccRecipients.forEach((i) => {
        mappedCcEmails = [...mappedCcEmails, i.email];
      });
      communication.templateData = [
        {
          key: 'claimIndx',
          value: claimIndx,
        },
        {
          key: 'claimNumber',
          value: claim.claimNumber,
        },
        {
          key: 'userName',
          value: whoProp,
        },
        {
          key: 'region',
          value: region?.toString(),
        },
        {
          key: 'regionId',
          value: region.toString(),
        },
        {
          key: 'phaseIndx',
          value: phaseIndxWithXA.toString(),
        },
        {
          key: 'pmContact',
          value: userContact,
        },
        {
          key: 'pmName',
          value: userInfo?.fullName,
        },
      ];

      if (selectedTimeSelector === TimeSelector.SpecificTime) {
        communication.templateData.push({ key: 'timeApproach',
          value: 'SpecificTime' });
        communication.templateData.push({ key: 'date',
          value: date ? new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }) : '' });
        communication.templateData.push({ key: 'time',
          value: time ? new Date(time).toLocaleTimeString('en-US', hourWithSecondsOptions) : '' });
      } else {
        communication.templateData.push({ key: 'timeApproach',
          value: 'TimeRange' });
        communication.templateData.push({ key: 'date',
          value: date ? new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }) : '' });
        communication.templateData.push({ key: 'timeStart',
          value: startTime ? new Date(startTime).toLocaleTimeString('en-US', hourWithSecondsOptions) : '' });
        communication.templateData.push({ key: 'timeEnd',
          value: endTime ? new Date(endTime).toLocaleTimeString('en-US', hourWithSecondsOptions) : '' });
      }
      
      if (template === MessageTemplate.AppointmentNotification || template === MessageTemplate.OnTheWayCrew) {
        communication.templateData.push({
          key: 'employeeName',
          value: whoPropEmployeeName,
        });
      }
      communication.messageTemplate = template;
      communication.messageChannel = type;
      communication.message = message;
      communication.sendToXact = isSendToXact;
      if (!claim?.projEmail && !isEdit && type === MessageChannel.Email) {
        communication.addEmailToClaim = true;
      }
      const recipientPhone = ((phone !== undefined) ? phone : undefined);
      if (recipientPhone === undefined && type === MessageChannel.SMS) {
        setSaveError(t('invalidPhoneNumber'));
        setIsLoading(false);
        return;
      }
      const recipient = type === MessageChannel.SMS ? recipientPhone : claim?.projEmail || contactEmail;
      if (type === MessageChannel.Email && !emailRegexNoGlobal.test(recipient)) {
        setSaveError(t('invalidNotificationEmailMessage'));
        setIsLoading(false);
        return;
      }
      communication.formattedNotifactionXact = formattedNotifactionXact(recipient, new Date().toLocaleString(), whoProp, type, message, mappedCcEmails);
      communication.recipients = [recipient];
      communication.isFrench = currentLang ? currentLang?.includes('fr') : i18n.language?.includes('fr');
      communication.ccRecipients = mappedCcEmails;
      const restClient = getRestClient(userState, ClientType.FORM);
      const resp = await restClient.post('/common/communication/send-communication', communication);
      if (resp.status === 200) {
        onSubmit(true);
        setShowSendToXaModal(false);
      } else {
        const responseMessage = t('saveClaimNotificationError');
        setSaveError(responseMessage);
      }
    } catch (e: any) {
      let msg: string;
      if (e?.response?.data?.ErrorCode === 400) {
        msg = t('errorXSS');
      } else {
        msg = e?.response?.data?.Description;
      }
      const responseMessage = msg || t('saveClaimNotificationError');
      setSaveError(responseMessage);
    }
    setIsLoading(false);
    setShowSendToXaModal(false);
  });
  const saveBtnClick = () => {
    onSubmitHandler();
  };
  const getEmailCCSuggestions = useCallback(
    (emailInput: string, isCc?: boolean) => {
      if (isCc) {
        if (emailInput.length > 0) {
          graphQLClient.request(GET_EMAIL_SUGGESTIONS, { term: emailInput }).then((res) => {
            const emailSuggestions = res?.userEmailsByTerm?.map((emailSuggestion: EmailSuggestion) => ({
              id: emailSuggestion.email,
              description: emailSuggestion.email,
              fullName: emailSuggestion.fullName,
            }));
            setCcSuggestions(emailSuggestions);
          });
        } else {
          setCcSuggestions([]);
        }
      }
    },
    [graphQLClient],
  );
  const getEmailSuggestions = useCallback(
    (emailInput: string) => {
      if (emailInput.length > 0) {
        graphQLClient.request(GET_EMAIL_SUGGESTIONS, { term: emailInput }).then((res) => {
          const emailSuggestions = res?.userEmailsByTerm?.map((emailSuggestion: EmailSuggestion) => ({
            id: emailSuggestion.email,
            description: emailSuggestion.email,
            fullName: emailSuggestion.fullName,
            phone: emailSuggestion.phone,
            phoneCell: emailSuggestion.phoneCell,
          }));
          setSuggestions(emailSuggestions);
        });
      } else {
        setSuggestions([]);
      }
    },
    [graphQLClient],
  );
  const onAddEmailClick = () => {
    // @ts-ignore
    const email = ccEmailInputRef.current.value;
    const testEmail = emailRegexNoGlobal.test(email !== undefined ? email : '');

    setInvalidCcEmailAlert(!testEmail);

    if (!testEmail) {
      return;
    }

    if (email && testEmail) {
      setCcRecipients([...ccRecipients, { email, id: tempValue, selected: true }]);
      setCcSuggestions([]);
      if (ccEmailInputRef?.current) {
        // @ts-ignore
        ccEmailInputRef.current.value = '';
      }
    }
  };
  const onOptionSelect = (option?: any) => {
    if (option) {
      setSelectedEmails([
        { email: option.description, id: tempValue, selected: true, fullName: option.fullName, phoneCell: option.phoneCell, phone: option.phone },
      ]);
    } else {
      // @ts-ignore
      setSelectedEmails([
        // @ts-ignore
        { email: emailInputRef.current.value, id: tempValue, selected: true, fullName: tempValue },
      ]);
    }
    setDefaultWho(option.fullName);
    setSuggestions([]);
  };
  const onCCOptionSelect = (option?: any, isCc?: boolean) => {
    if (isCc) {
      if (option) {
        setCcRecipients([
          ...ccRecipients,
          { email: option.description, id: tempValue, selected: true, fullName: option.fullName },
        ]);
      } else {
        // @ts-ignore
        setCcRecipients([
          ...ccRecipients,
          // @ts-ignore
          { email: ccEmailInputRef.current.value, id: tempValue, selected: true, fullName: tempValue },
        ]);
      }
    }
    setCcSuggestions([]);
    if (ccEmailInputRef?.current) {
      // @ts-ignore
      ccEmailInputRef.current.value = '';
    }
  };

  const onCustomContactChange = useCallback(
    (event: any, contact: CustomEmailContact) => {
      if (event.detail.checked) {
        const filteredSelectedEmails = selectedEmails.filter((selected) => selected.email !== contact.email);
        const newSelectedEmails = [...filteredSelectedEmails, { ...contact, selected: true }];

        setSelectedEmails(newSelectedEmails);
      } else if (contact.id === tempValue) {
        const filteredSelectedEmails = selectedEmails.filter((selected) => selected.email !== contact.email);
        const newSelectedEmails = [...filteredSelectedEmails, { ...contact, selected: false }];

        setSelectedEmails(newSelectedEmails);
      } else {
        const updatedSelectedEmails = selectedEmails.filter((selected) => selected.email !== contact.email);

        setSelectedEmails(updatedSelectedEmails);
      }
    },
    [selectedEmails],
  );

  // TODO: ADD "DO YOU WANT TO DELETE" MODAL
  const removeCcRecipient = (email: string) => {
    setCcRecipients(ccRecipients.filter((recipient) => recipient.email !== email));
  };

  useEffect(() => {
    register('recipients', { value: selectedEmails?.map((r) => r.email) });
    register('ccRecipients', { value: ccRecipients?.map((r) => r.email) });
  }, [selectedEmails, ccRecipients, register]);

  
  const enableSave = !isEdit;

  let templateOptions: Option[];
  if (isEdit) {
    templateOptions = [{ label: t('initialNotification'), value: MessageTemplate.ArrivalConfirmation },
      { label: t('appointmentNotification'), value: MessageTemplate.AppointmentNotification },
      { label: t('onTheWayCrew'), value: MessageTemplate.OnTheWayCrew },
    ];

  } else if (isCrewNotificationsEnabled) {
    templateOptions = [
      { label: t('onTheWayCrew'), value: MessageTemplate.OnTheWayCrew },
      { label: t('appointmentNotification'), value: MessageTemplate.AppointmentNotification },
    ];
  } else {
    templateOptions = [
      { label: t('initialNotification'), value: MessageTemplate.ArrivalConfirmation },
      { label: t('appointmentNotification'), value: MessageTemplate.AppointmentNotification },
    ];
  }


  useEffect(() => {
    if (isEdit) {
      const messageTemplate = notification?.metadata?.find(
        (n: { key: string; value: string }) => n.key === 'messageTemplate',
      );
      if (messageTemplate?.value === MessageTemplate.AppointmentNotification) {
        setValue('template', MessageTemplate.AppointmentNotification);
      } else if (messageTemplate?.value === MessageTemplate.OnTheWayCrew) {
        setValue('template', MessageTemplate.OnTheWayCrew);
      } else {
        setValue('template', MessageTemplate.ArrivalConfirmation);
      }

      if (notification?.sentToXa) {
        setIsSendToXact(true);
      }
      setValue('message', notification.body);
      const type = notification?.messageType === 'MailMessage' ? MessageChannel.Email : MessageChannel.SMS;
      setValue('type', type);
      if (type === MessageChannel.SMS) {
        setValue('phone', notification.recipients[0]);
      }

      const who = notification?.metadata?.find((n: { key: string; value: string }) => n.key === 'userName');
      if (who?.value) {
        setSelectedEmails([{ email: who.value, id: tempValue, selected: true }]);
      }
      const dateselected = notification?.metadata?.find(
        (n: { key: string; value: string }) => n.key === 'date',
      );
      if (dateselected?.value) {
        setValue('date', dateselected.value);
      }
      const timeApproach = notification?.metadata?.find(
        (n: { key: string; value: string }) => n.key === 'timeApproach',
      );
      if (timeApproach?.value === 'SpecificTime') {
        setSelectedTimeSelector(TimeSelector.SpecificTime);
        const timeSelected = notification?.metadata?.find(
          (n: { key: string; value: string }) => n.key === 'time',
        );
        if (timeSelected?.value) {
          setValue('time', convertStringTimeToDateTime(timeSelected.value).toString());
        }
      } else {
        setSelectedTimeSelector(TimeSelector.TimeRange);
        const startTimeSelected = notification?.metadata?.find(
          (n: { key: string; value: string }) => n.key === 'timeStart',
        );
        if (startTimeSelected?.value) {
          setValue('startTime', convertStringTimeToDateTime(startTimeSelected.value).toString());
        }
        const endTimeSelected = notification?.metadata?.find(
          (n: { key: string; value: string }) => n.key === 'timeEnd',
        );
        if (endTimeSelected?.value) {
          setValue('endTime', convertStringTimeToDateTime(endTimeSelected.value).toString());
        }
      }

    }
  }, [isEdit, notification, template, setValue, isCrewNotificationsEnabled]);

  const textForArrivalConfirmation = () => {
    const fullName = selectedEmails?.length > 0 ? selectedEmails[0].fullName : userInfo?.fullName;
    const isFrench = currentLang ? currentLang?.includes('fr') : i18n.language?.includes('fr');
    const templateT = isFrench ? tFr : tEn;
    const arrivalConfirmationText = selectedTimeSelector === TimeSelector.SpecificTime ? 
      templateT('ArrivalConfirmationMessageSpecific').toString()
        .replace('#Date#', date ? new Date(date).toLocaleDateString(isFrench ? 'fr' : 'en', dayOptions) : '')
        .replace('#Time#', time ? new Date(time).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '')
      : 
      templateT('ArrivalConfirmationMessageRange').toString()
        .replace('#Date#', date ? new Date(date).toLocaleDateString(isFrench ? 'fr' : 'en', dayOptions) : '')
        .replace('#StartTime#', startTime ? new Date(startTime).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '')
        .replace('#EndTime#', endTime ? new Date(endTime).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '');
    const arrivalConfirmationTextLine2 = templateT('ArrivalConfirmationMessageLine2').toString()
      .replace('#UserFullName#', fullName || '')
      .replace('#UserContact#', userContact || '');
    return (
    <>
      <IonText className="notification-template-message">{`${templateT('Hello').toString()},`}</IonText>
      <br /><br />
      <IonText className="notification-template-message">{arrivalConfirmationText || ''}</IonText>
      <br /><br />
      <IonText className="notification-template-message">{arrivalConfirmationTextLine2 || ''}</IonText>
      <br /><br />
      <IonText className="notification-template-message">{templateT('ArrivalConfirmationMessageLine3').toString() || ''}</IonText>
      <br /><br />
      <IonText className="notification-template-message">{`${templateT('ThankYou').toString()},`}</IonText>
      <br /><br />
      <IonText className="notification-template-message">{`${templateT('FosDoNotReply').toString()}.`}</IonText>
    </>
    );
  };

  const textForAppointmentNotification = () => {
    const fullName = selectedEmails?.length > 0 ? selectedEmails[0].fullName : userInfo?.fullName;
    const isFrench = currentLang ? currentLang?.includes('fr') : i18n.language?.includes('fr');
    const templateT = isFrench ? tFr : tEn;
    const arrivalConfirmationText = selectedTimeSelector === TimeSelector.SpecificTime ? 
      templateT('AppointmentNotificationSpecific').toString()
        .replace('#UserFullName#', fullName || '')
        .replace('#UserContact#', userContact || '')
        .replace('#Date#', date ? new Date(date).toLocaleDateString(isFrench ? 'fr' : 'en', dayOptions) : '')
        .replace('#Time#', time ? new Date(time).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '')
      : 
      templateT('AppointmentNotificationRange').toString()
        .replace('#Date#', date ? new Date(date).toLocaleDateString(isFrench ? 'fr' : 'en', dayOptions) : '')
        .replace('#StartTime#', startTime ? new Date(startTime).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '')
        .replace('#EndTime#', endTime ? new Date(endTime).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '');

    const arrivalConfirmationTextLine2 = templateT('AppointmentNotificationLine2').toString()
      .replace('#UserFullName#', fullName || '')
      .replace('#UserContact#', userContact || '');
    return (
      <>
        <IonText className="notification-template-message">{arrivalConfirmationText || ''}</IonText>
        <br /><br />
        <IonText className="notification-template-message">{arrivalConfirmationTextLine2 || ''}</IonText>
        <br /><br />
        <IonText className="notification-template-message">{`${templateT('ThankYou').toString()},`}</IonText>
        <br /><br />
        <IonText className="notification-template-message">{`${templateT('FosDoNotReply').toString()}.`}</IonText>
      </>
    );
  };

  const textForOnTheWayCrew = () => {
    const isFrench = currentLang ? currentLang?.includes('fr') : i18n.language?.includes('fr');
    const templateT = isFrench ? tFr : tEn;
    const onTheWayCrewText = selectedTimeSelector === TimeSelector.SpecificTime ? 
      templateT('OnTheWayCrewMessageSpecific').toString()
        .replace('#Date#', date ? new Date(date).toLocaleDateString(isFrench ? 'fr' : 'en', dayOptions) : '')
        .replace('#Time#', time ? new Date(time).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '')
      : 
      templateT('OnTheWayCrewMessageRange').toString()
        .replace('#Date#', date ? new Date(date).toLocaleDateString(isFrench ? 'fr' : 'en', dayOptions) : '')
        .replace('#StartTime#', startTime ? new Date(startTime).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '')
        .replace('#EndTime#', endTime ? new Date(endTime).toLocaleTimeString(isFrench ? 'fr' : 'en', hourOptions) : '');
    return (
      <>
        <IonText className="notification-template-message">{`${templateT('Hello').toString()},`}</IonText>
        <br /><br />
        {// eslint-disable-next-line no-unsafe-optional-chaining
          <IonText className="notification-template-message">{onTheWayCrewText || ''}</IonText>}
        <br /><br />
        <IonText className="notification-template-message">{`${templateT('ThankYou').toString()},`}</IonText>
        <br /><br />
        <IonText className="notification-template-message">{`${templateT('FosDoNotReply').toString()}.`}</IonText>
      </>
    );
  };

  const languageOptions = Object.keys(languages).map((lng) => ({ label: t(lng).toString(), value: lng }));

  return (
    <>
      <IonContent className="ion-padding">
        <form id="addNotesForm" onSubmit={onSubmitHandler}>
          <FosSelectItem
            disabled={isEdit}
            label={t('type')}
            options={typeOptions}
            {...register('type')}
            error={errors.type?.message}
          />
          {type === MessageChannel.SMS && (
            <Controller
              control={control}
              name="phone"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <IonItem
                  lines="none"
                  className={`fos-stacked-input-item ion-margin-bottom ${error ? 'ion-invalid' : ''}`.trim()}
                >
                  <IonInput
                    type="tel"
                    readonly={isEdit}
                    ref={async (phoneInput) => {
                      if (phoneInput) {
                        const input = await phoneInput.getInputElement();
                        phoneMask(input);
                      }
                    }}
                    className="fos-stacked-input"
                    placeholder={t('phoneNumberPlaceHolder')}
                    defaultValue={defaultPhone}
                    value={value}
                    onChange={onChange}
                    onIonChange={onChange}
                  />
                  {error?.message && <IonNote slot="error">{error.message}</IonNote>}
                </IonItem>
              )}
            />
          )}
          <FosSelectItem
            label={t('Template')}
            disabled={isEdit}
            options={templateOptions}
            {...register('template')}
            error={errors.template?.message}
          />
          <Controller
            control={control}
            name="date"
            render={({
              field: { onChange, value, name },
              fieldState: { error },
            }) => (
              <>
                <IonItem id="date-trigger" lines="none" className='fos-select-date-item ion-margin-top'>
                  <div className="fos-select-date-border" />
                  <IonLabel id="date-input" className="ion-text-wrap">
                    <IonText className='fos-select-date-label'>{t('date').toString()} *</IonText>
                  </IonLabel>
                  <IonText className='fos-select-date-label'>{moment(value || null).isValid() ? moment(value).format('MMMM Do YYYY') : ''}</IonText>
                  <IonModal trigger='date-trigger' className='center fixed-300-width'>
                    <IonDatetime
                      disabled={isEdit}
                      showDefaultButtons
                      showClearButton
                      presentation="date"
                      id='date'
                      name={name}
                      size='cover'
                      value={value || null}
                      onIonChange={(e) => { clearErrors('date'); onChange(e); }}
                    />
                  </IonModal>
                </IonItem>
                <p className="ion-no-margin ion-padding-start ion-margin-bottom">
                  {error &&
                    <small>
                      <IonNote color="danger">{error.message}</IonNote>
                    </small>
                  }
                </p>
              </>
            )}
          />
          <IonRadioGroup defaultValue={TimeSelector.SpecificTime} value={selectedTimeSelector}>
            <IonItem className='radio-label' style={{ '--border-width': 0, '--inner-border-width': 0 }}>
              <IonLabel>{t('specificTime').toString()}</IonLabel>
              <IonRadio disabled={isEdit} slot="start" onClick={() => setSelectedTimeSelector(TimeSelector.SpecificTime)} value={TimeSelector.SpecificTime} />
            </IonItem>
            <IonItem className='radio-label' style={{ '--border-width': 0, '--inner-border-width': 0 }}>
              <IonLabel>{t('timeRange').toString()}</IonLabel>
              <IonRadio disabled={isEdit} slot="start" onClick={() => setSelectedTimeSelector(TimeSelector.TimeRange)} value={TimeSelector.TimeRange} />
            </IonItem>
          </IonRadioGroup>
          {selectedTimeSelector === TimeSelector.SpecificTime && 
            <Controller
            control={control}
            name="time"
            render={({
              field: { onChange, value, name },
              fieldState: { error },
            }) => (
              <>
                <IonItem id="time-trigger" lines="none" className='fos-select-date-item ion-margin-top'>
                  <div className="fos-select-date-border" />
                  <IonLabel id="loss-date-input" className="ion-text-wrap">
                    <IonText className='fos-select-date-label'>{t('time').toString()} *</IonText>
                  </IonLabel>
                  <IonText className='fos-select-date-label'>{moment(value || null).isValid() ? moment(value).format('hh:mm A') : ''}</IonText>
                  <IonModal trigger='time-trigger' className='center fixed-300-width'>
                    <IonDatetime
                      disabled={isEdit}
                      showDefaultButtons
                      showClearButton
                      presentation="time"
                      id='time'
                      name={name}
                      size='cover'
                      value={value || null}
                      onIonChange={(e) => { clearErrors('time'); onChange(e); }}
                    />
                  </IonModal>
                </IonItem>
                <p className="ion-no-margin ion-padding-start ion-margin-bottom">
                  {error &&
                    <small>
                      <IonNote color="danger">{error.message}</IonNote>
                    </small>
                  }
                </p>
              </>
            )}
          />
          }
          {selectedTimeSelector === TimeSelector.TimeRange && 
          <>
            <Controller
            control={control}
            name="startTime"
            render={({
              field: { onChange, value, name },
              fieldState: { error },
            }) => (
              <>
                <IonItem id="startTime-trigger" lines="none" className='fos-select-date-item ion-margin-top'>
                  <div className="fos-select-date-border" />
                  <IonLabel id="loss-date-input" className="ion-text-wrap">
                    <IonText className='fos-select-date-label'>{t('startTime').toString()} *</IonText>
                  </IonLabel>
                  <IonText className='fos-select-date-label'>{moment(value || null).isValid() ? moment(value).format('hh:mm A') : ''}</IonText>
                  <IonModal trigger='startTime-trigger' className='center fixed-300-width'>
                    <IonDatetime
                      disabled={isEdit}
                      showDefaultButtons
                      showClearButton
                      presentation="time"
                      id='startTime'
                      name={name}
                      size='cover'
                      value={value || null}
                      onIonChange={(e) => { clearErrors('startTime'); onChange(e); }}
                    />
                  </IonModal>
                </IonItem>
                <p className="ion-no-margin ion-padding-start ion-margin-bottom">
                  {error &&
                    <small>
                      <IonNote color="danger">{error.message}</IonNote>
                    </small>
                  }
                </p>
              </>
            )}
          />
          <Controller
            control={control}
            name="endTime"
            render={({
              field: { onChange, value, name },
              fieldState: { error },
            }) => (
              <>
                <IonItem id="endTime-trigger" lines="none" className='fos-select-date-item ion-margin-top'>
                  <div className="fos-select-date-border" />
                  <IonLabel id="loss-date-input" className="ion-text-wrap">
                    <IonText className='fos-select-date-label'>{t('endTime').toString()} *</IonText>
                  </IonLabel>
                  <IonText className='fos-select-date-label'>{moment(value || null).isValid() ? moment(value).format('hh:mm A') : ''}</IonText>
                  <IonModal trigger='endTime-trigger' className='center fixed-300-width'>
                    <IonDatetime
                      disabled={isEdit}
                      showDefaultButtons
                      showClearButton
                      presentation="time"
                      id='endTime'
                      name={name}
                      size='cover'
                      value={value || null}
                      onIonChange={(e) => { clearErrors('endTime'); onChange(e); }}
                    />
                  </IonModal>
                </IonItem>
                <p className="ion-no-margin ion-padding-start ion-margin-bottom">
                  {error &&
                    <small>
                      <IonNote color="danger">{error.message}</IonNote>
                    </small>
                  }
                </p>
              </>
            )}
          />
          </>
          }
          <FosSelectItem
            label={t('notificationLanguage').toString()}
            disabled={isEdit}
            options={languageOptions}
            value={currentLang}
            onChange={(lng) => {
              setCurrentLang(lng.target.value);
            }}
          />
          {isWhoEnable && 
          <AutocompleteSearch
            disabled={isEdit}
            ref={emailInputRef}
            placeholder={`${t('who').toString()}`}
            onChange={(evt: any) => {
              getEmailSuggestions(evt.target.value);
              if (errors.who) {
                errors.who.message = '';
              }
            }}
            options={suggestions}
            onOptionSelect={(option) => onOptionSelect(option)}
            debounce={1000}
            error={errors.who?.message}
            value={defaultWho || ''}
          />}
          <div className='message-template-div ion-margin-top'>
            <IonLabel>
              <IonText className="fos-share-with-contact-title">{t('messageTemplate').toString()}</IonText>
            </IonLabel>
          </div>
          <div className='message-template-div'>
            {isEdit &&
              <ReactQuill
                className='no-format-toolbar'
                onFocus={() => window.scrollTo({ top: 50 })}
                theme="snow"
                readOnly
                value={notification.body}
              />}
            {isArrivalConfirmationSelected && !isEdit &&
              textForArrivalConfirmation()
            }
            {(isAppointmentNotificationSelected) && !isEdit &&
              textForAppointmentNotification()
            }
            {(isOnTheWayCrewSelected) && !isEdit &&
              textForOnTheWayCrew()
            }
          </div>
          <IonToast
            isOpen={!!saveError}
            duration={3000}
            message={saveError}
            buttons={[{ role: 'cancel', icon: closeOutline }]}
            onDidDismiss={() => {
              setSaveError('');
            }}
          />
          {type === MessageChannel.Email && (
            <div className="sent-emails">
              {(claim?.projEmail || isEdit) && (
                <>
                  <IonLabel>
                    <IonText className="fos-share-with-contact-title">{t('messageTo').toString()}:</IonText>
                  </IonLabel>
                  <IonList>
                    <IonItem lines="none" className="ion-no-padding">
                      <IonLabel>
                        {notification?.recipients && notification.recipients[0] ? (
                          <IonText className="fos-share-with-contact-value">{notification.recipients[0]}</IonText>
                        ) : (
                          <div>
                            <IonText>{claim?.contactName}</IonText>
                            {claim?.contactName && ' - '}
                            <IonText className="fos-share-with-contact-value">{claim?.projEmail}</IonText>
                          </div>
                        )}
                      </IonLabel>
                    </IonItem>
                  </IonList>
                </>
              )}
              {!claim?.projEmail && !isEdit && type === MessageChannel.Email && (
                <Controller
                  control={control}
                  name="contactEmail"
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <div style={{ marginBottom: 20 }}>
                      <div className='border-input'>
                        <IonInput
                          name={name}
                          className="fos-stacked-input fos-stacked-custom-input"
                          type='text'
                          placeholder={`${t('onsiteContacteEmail')  } *`}
                          value={value}
                          onChange={onChange}
                          onIonChange={(e) => { clearErrors('contactEmail'); onChange(e); }}
                        />
                      </div>
                      {error?.message && <IonNote slot="error" className='error-msg'>{error?.message}</IonNote>}
                    </div>
                  )}
                />
              )}
              {type === MessageChannel.Email && (
                <>
                  {!isEdit && (
                    <AutocompleteSearch
                      disabled={isEdit}
                      ref={ccEmailInputRef}
                      placeholder={`${t('CcEmails').toString()}`}
                      onChange={(evt: any) => {
                        getEmailCCSuggestions(evt.target.value, true);
                      }}
                      options={ccSuggestions}
                      onOptionSelect={(option) => onCCOptionSelect(option, true)}
                      debounce={1000}
                      addButton
                      onAddClick={() => onAddEmailClick()}
                      disableEnter
                    />
                  )}
                  {isInvalidCcEmailAlertOpen && (
                    <IonText className="invalid-entry-message">{t('pleaseEnterAValidEmailAddress').toString()}</IonText>
                  )}
                  {ccRecipients.length > 0 && (
                    <div className="selected-section recents-section recents-label">
                      <IonLabel>{t('selectedCcEmails').toString()}</IonLabel>
                      <FormItemDivider />
                      <IonList>
                        {ccRecipients.map((contact, index) => (
                          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                          <div key={`${contact.email + index}`} onClick={() => removeCcRecipient(contact.email)}>
                            <IonItem lines="none" className="ion-no-padding">
                              <IonCheckbox
                                disabled
                                className="form-item-checkbox"
                                slot="start"
                                color="primary"
                                checked={contact.selected!}
                                onIonChange={(e) => onCustomContactChange(e, contact)}
                              />
                              <IonLabel className="fos-share-with-contact-value-label">
                                <IonText className="fos-share-with-contact-value">{contact.email}</IonText>
                              </IonLabel>
                            </IonItem>
                            <FormItemDivider />
                          </div>
                        ))}
                      </IonList>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </form>
        <IonLoading isOpen={isFetching} message={t('loading')} duration={2000} />

        <IonModal id="send-to-xa-modal" ref={generateModal} isOpen={showSendToXaModal} onDidDismiss={() => setShowSendToXaModal(false)}>
          <IonHeader>
            <h1 className="h1-center">{t('submitToXact').toString()}</h1>
          </IonHeader>
          <IonContent className="wrapper" id="phase-content">
            <IonItem className="ion-item-border" lines="none">
              <IonLabel>{t('phase').toString()}</IonLabel>
              <IonSelect
                value={phaseIndxWithXA}
                onIonChange={(event) => setPhaseIndxWithXA(event.detail.value)}
              >
                {claim?.phases?.filter((phase: { linkedToXA: any; }) => phase.linkedToXA).map((phase: { phaseIndx: React.Key | null | undefined; phaseCode: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactFragment | React.ReactPortal | Iterable<ReactI18NextChild> | null | undefined; }) => (
                  <IonSelectOption key={phase.phaseIndx} value={phase.phaseIndx}>
                    {phase.phaseCode}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
          </IonContent>
          <IonFooter className="phase-footer">
            <IonButton className="generate-btn"
              onClick={() => {
                if (phaseIndxWithXA !== 0) {
                  saveBtnClick();
                } else {
                  setSaveError(t('selectXaPhase').toString());
                }
              }}
            >
              {t('submit').toString()}
            </IonButton>
          </IonFooter>
        </IonModal>

      </IonContent>
      <IonFooter className="ion-padding claim-notes-form-footer">
        <IonButton disabled={!enableSave} expand="block" fill="solid" onClick={async () => {
          if (isSendToXact) {
            const output = await trigger();
            if (output) {
              setShowSendToXaModal(true);
            }
          } else { saveBtnClick(); }
        }}>
          {t('send').toString()}
        </IonButton>
      </IonFooter>
      <IonLoading isOpen={isLoading} message={t('saving')} duration={20000} />
    </>
  );
};


export default ClaimNotificationForm;